import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { buildApiUrl } from "../config/api";

const sslPlans = [
  {
    title: "Managed SSL Setup",
    serviceKey: "managed_ssl",
    price: 999,
    bestFor: "Domains hosted or managed by Asiduo",
    features: ["DNS review", "HTTPS activation", "Auto-renewal monitoring"],
  },
  {
    title: "External Domain SSL",
    serviceKey: "external_ssl",
    price: 1499,
    bestFor: "Domains bought outside Asiduo",
    features: ["Ownership validation", "TXT/HTTP instructions", "Certificate installation support"],
  },
  {
    title: "Wildcard SSL Planning",
    serviceKey: "wildcard_ssl",
    price: 2499,
    bestFor: "Multiple subdomains",
    features: ["DNS-01 validation plan", "Subdomain coverage", "Renewal checklist"],
  },
];

const normalizeDomain = (value) =>
  value
    .toLowerCase()
    .trim()
    .replace(/^https?:\/\//, "")
    .replace(/\/.*$/, "");

const isValidDomain = (value) =>
  /^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/.test(value);

const Ssl = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [domainName, setDomainName] = useState(normalizeDomain(location.state?.domainName || ""));
  const [domainSource, setDomainSource] = useState(location.state?.domainSource || "external");
  const [sslCheck, setSslCheck] = useState(null);
  const [sslCheckLoading, setSslCheckLoading] = useState(false);
  const [sslCheckError, setSslCheckError] = useState("");
  const cleanedDomain = useMemo(() => normalizeDomain(domainName), [domainName]);
  const canBuy = isValidDomain(cleanedDomain);

  const startCheckout = (plan) => {
    if (!canBuy) {
      return;
    }

    navigate("/payment/razorpay", {
      state: {
        orderType: "ssl",
        serviceCategory: "ssl",
        serviceKey: plan.serviceKey,
        amount: plan.price,
        domainName: cleanedDomain,
        domainSource,
        setupPreference: plan.title,
      },
    });
  };

  const checkSslStatus = async () => {
    if (!canBuy) {
      setSslCheckError("Enter a valid domain before checking SSL.");
      return;
    }

    setSslCheckLoading(true);
    setSslCheck(null);
    setSslCheckError("");

    try {
      const response = await fetch(
        buildApiUrl(`/api/ssl-check?domain=${encodeURIComponent(cleanedDomain)}`)
      );
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Unable to check SSL.");
      }

      setSslCheck(data);
    } catch (error) {
      setSslCheckError(error.message);
    } finally {
      setSslCheckLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-950 via-slate-950 to-slate-900 px-6 py-28 text-white">
      <div className="mx-auto max-w-7xl">
        <div className="grid items-center gap-16 lg:grid-cols-[1fr_420px]">
          <div>
            <motion.div
              animate={{ y: [0, -18, 0] }}
              transition={{ duration: 4, repeat: Infinity }}
              className="text-7xl"
            >
              SSL
            </motion.div>

            <h1 className="mt-8 text-5xl font-black leading-tight md:text-7xl">
              Secure Websites With
              <span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
                {" "}SSL
              </span>
            </h1>

            <p className="mt-8 max-w-4xl text-lg leading-8 text-slate-300">
              SSL depends on domain control. If Asiduo manages DNS or hosting, we can
              issue and renew the certificate after records point correctly. If the
              domain is external, the customer must publish validation records first.
            </p>
          </div>

          <section className="rounded-[35px] border border-white/10 bg-white/10 p-8 backdrop-blur-xl">
            <h2 className="text-2xl font-black">SSL Domain Details</h2>

            <input
              type="text"
              value={domainName}
              onChange={(event) => setDomainName(event.target.value)}
              placeholder="example.com"
              className="mt-6 h-14 w-full rounded-2xl border border-white/10 bg-white/10 px-5 text-white outline-none placeholder:text-slate-400 focus:border-emerald-400"
            />

            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => setDomainSource("asiduo_registered")}
                className={`h-12 rounded-2xl text-sm font-bold ${
                  domainSource === "asiduo_registered"
                    ? "bg-emerald-500 text-emerald-950"
                    : "border border-white/20 bg-white/10 text-white"
                }`}
              >
                Managed DNS
              </button>

              <button
                type="button"
                onClick={() => setDomainSource("external")}
                className={`h-12 rounded-2xl text-sm font-bold ${
                  domainSource === "external"
                    ? "bg-emerald-500 text-emerald-950"
                    : "border border-white/20 bg-white/10 text-white"
                }`}
              >
                External DNS
              </button>
            </div>

            {!canBuy && domainName ? (
              <p className="mt-4 text-sm font-semibold text-rose-300">
                Enter a valid domain such as example.com.
              </p>
            ) : null}

            <button
              type="button"
              onClick={checkSslStatus}
              disabled={!canBuy || sslCheckLoading}
              className="mt-6 w-full rounded-2xl border border-emerald-300/50 bg-emerald-500/15 px-6 py-3 text-sm font-bold text-white transition hover:bg-emerald-500/25 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {sslCheckLoading ? "Checking SSL..." : "Check SSL Status"}
            </button>

            {sslCheckError ? (
              <p className="mt-4 text-sm font-semibold text-rose-300">{sslCheckError}</p>
            ) : null}
          </section>
        </div>

        {sslCheck ? (
          <section className="mt-12 rounded-[35px] border border-white/10 bg-white/10 p-8 backdrop-blur-xl">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-sm font-bold uppercase tracking-[0.2em] text-emerald-300">
                  Live SSL Status
                </p>
                <h2 className="mt-2 text-3xl font-black">{sslCheck.domain}</h2>
              </div>

              <span
                className={`w-fit rounded-full px-5 py-2 text-sm font-bold ${
                  sslCheck.readiness.certificateTrusted
                    ? "bg-emerald-400 text-emerald-950"
                    : "bg-amber-400 text-amber-950"
                }`}
              >
                {sslCheck.readiness.certificateTrusted ? "Trusted HTTPS" : "Needs Setup"}
              </span>
            </div>

            <div className="mt-8 grid gap-4 md:grid-cols-3">
              {[
                ["Website DNS", sslCheck.readiness.dnsReadyForSsl, "A, AAAA, or CNAME record exists"],
                ["HTTPS Reachable", sslCheck.readiness.httpsReachable, "Port 443 responded"],
                ["Trusted Certificate", sslCheck.readiness.certificateTrusted, "Certificate validates for browsers"],
              ].map(([label, ready, detail]) => (
                <div
                  key={label}
                  className={`rounded-2xl border p-5 ${
                    ready
                      ? "border-emerald-400/30 bg-emerald-500/10"
                      : "border-amber-400/30 bg-amber-500/10"
                  }`}
                >
                  <div className="flex items-center justify-between gap-4">
                    <h3 className="font-bold">{label}</h3>
                    <span className={ready ? "text-emerald-300" : "text-amber-300"}>
                      {ready ? "OK" : "Pending"}
                    </span>
                  </div>
                  <p className="mt-3 text-sm text-slate-300">{detail}</p>
                </div>
              ))}
            </div>

            <div className="mt-6 grid gap-4 lg:grid-cols-2">
              <div className="rounded-2xl border border-white/10 bg-slate-950/50 p-5">
                <h3 className="font-bold text-emerald-300">DNS Records</h3>
                <div className="mt-4 space-y-2 break-words text-sm text-slate-300">
                  <p>A: {sslCheck.dns.a.length ? sslCheck.dns.a.join(", ") : "Not found"}</p>
                  <p>AAAA: {sslCheck.dns.aaaa.length ? sslCheck.dns.aaaa.join(", ") : "Not found"}</p>
                  <p>CNAME: {sslCheck.dns.cname.length ? sslCheck.dns.cname.join(", ") : "Not found"}</p>
                </div>
              </div>

              <div className="rounded-2xl border border-white/10 bg-slate-950/50 p-5">
                <h3 className="font-bold text-emerald-300">Certificate</h3>
                <div className="mt-4 space-y-2 break-words text-sm text-slate-300">
                  <p>Issuer: {sslCheck.https.issuer?.O || sslCheck.https.issuer?.CN || "Not available"}</p>
                  <p>Valid To: {sslCheck.https.validTo || "Not available"}</p>
                  <p>
                    Days Remaining:{" "}
                    {Number.isFinite(sslCheck.https.daysRemaining)
                      ? sslCheck.https.daysRemaining
                      : "Not available"}
                  </p>
                  {sslCheck.https.authorizationError ? (
                    <p className="text-amber-300">Issue: {sslCheck.https.authorizationError}</p>
                  ) : null}
                </div>
              </div>
            </div>
          </section>
        ) : null}

        <section className="mt-20 grid gap-6 md:grid-cols-3">
          {[
            ["Hosted With Us", "Point A/CNAME records to hosting, then issue HTTPS automatically."],
            ["External Domain", "Send TXT or HTTP validation records, wait for DNS propagation, then install SSL."],
            ["Wildcard SSL", "Use DNS-01 validation for wildcard coverage such as *.example.com."],
          ].map(([title, body]) => (
            <article
              key={title}
              className="rounded-3xl border border-white/10 bg-white/10 p-8 backdrop-blur-xl shadow-[0_0_40px_rgba(16,185,129,0.2)]"
            >
              <h2 className="text-2xl font-bold">{title}</h2>
              <p className="mt-4 leading-7 text-slate-300">{body}</p>
            </article>
          ))}
        </section>

        <section className="mt-20">
          <h2 className="text-center text-4xl font-black md:text-6xl">SSL Services</h2>

          <div className="mt-16 grid gap-8 md:grid-cols-3">
            {sslPlans.map((plan) => (
              <motion.article
                key={plan.serviceKey}
                whileHover={{ y: -8, scale: 1.03 }}
                className="rounded-[35px] border border-white/10 bg-white/10 p-8 backdrop-blur-xl"
              >
                <p className="text-sm font-bold uppercase tracking-[0.2em] text-emerald-300">
                  {plan.bestFor}
                </p>
                <h3 className="mt-4 text-3xl font-black">{plan.title}</h3>
                <p className="mt-6 text-5xl font-black text-emerald-300">
                  Rs. {plan.price}
                </p>

                <div className="mt-8 space-y-4 text-slate-300">
                  {plan.features.map((feature) => (
                    <p key={feature}>{feature}</p>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={() => startCheckout(plan)}
                  disabled={!canBuy}
                  className="mt-10 w-full rounded-2xl bg-gradient-to-r from-emerald-500 to-cyan-500 py-4 font-bold text-slate-950 transition-all duration-300 hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Buy SSL Setup
                </button>
              </motion.article>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default Ssl;
